import ReviewsV2Container from './v2/ReviewsV2Container';
import { Box } from '@mui/material';
import { useDispatch } from 'react-redux';
import { setReviewsAnalysisResults } from 'store/reducers/actions';
import { useEffect } from 'react';

const exempleReport = {
  url: 'semrush.com',
  review_sources: {
    g2: 'https://www.g2.com/products/semrush/',
    trustpilot: 'https://www.trustpilot.com/review/semrush.com'
  },
  review_analysis_results: {
    negative_issues: {
      'Poor Customer Support': {
        impact: 'group',
        severity: 'high',
        frequency: 70,
        business_priority: 'high',
        representative_quotes: [
          'No reply from support.',
          'I have been sent from pillar to post, with no solution in sight. No one is willing to resolve my issue.'
        ]
      },
      'Data Accuracy Concerns': {
        impact: 'individual',
        severity: 'medium',
        frequency: 12,
        business_priority: 'medium',
        representative_quotes: [
          'The features I originally subscribed to SEMrush for are no longer included in the Guru plan. Now, you have to pay extra for them again.',
          "The platform's organic traffic data is far from accurate, and users should exercise caution before relying on it for crucial decision-making."
        ]
      },
      'Difficult Cancellation Process': {
        impact: 'all_users',
        severity: 'high',
        frequency: 110,
        business_priority: 'high',
        representative_quotes: [
          'Deliberately difficult to cancel. There is no obvious way to cancel from within the app. I had to google how to do it.',
          "Overly Complicated and Difficult to Cancel. Semrush might be useful for some, but I found it overly complicated. While complexity itself wouldn't be an issue, cancelling the subscription before the trial ended proved unnecessarily difficult—it felt deliberately challenging, almost as if designed to keep users subscribed."
        ]
      },
      'Aggressive Upselling & Hidden Fees': {
        impact: 'group',
        severity: 'medium',
        frequency: 35,
        business_priority: 'medium',
        representative_quotes: [
          'Feels like you are always nickel and dimed. Local SEO tracking is also way too expensive.',
          'Service is great, however I have been charged numerous times for unclear additional payment extras that I unknowingly signed up for as they are snuck in across the platform and worded in a highly misleading manner.'
        ]
      },
      'Unauthorized Charges & Refund Denials': {
        impact: 'group',
        severity: 'high',
        frequency: 40,
        business_priority: 'high',
        representative_quotes: [
          'I was charged over $4,000 for an annual SEMrush renewal without receiving any notice, reminder, or invoice.',
          "I signed up for a free trial with Semrush but didn't realize it would auto-renew into a $139/month paid plan — there was no clear reminder or warning."
        ]
      }
    },
    overall_summary: {
      neutral_reviews: 50,
      negative_reviews: 140,
      positive_reviews: 210,
      total_reviews_analyzed: 400,
      overall_sentiment_score: 0.12
    },
    product_aspects: {
      'Pricing & Value': {
        key_insights:
          'Perceived as expensive, with hidden fees and constant upselling; value for money questioned, especially for small businesses.',
        quote_source: {
          platform: 'trustpilot',
          review_id: '67781a043eeb6095a5c10499'
        },
        mentions_count: 90,
        sentiment_score: -0.45,
        business_priority: 'high',
        representative_quote: [
          'Very deceptive pricing. Hidden fees everywhere. They entice with a lowball price but to get any effective services you need to add on very expensive additional services.'
        ]
      },
      'Customer Support': {
        key_insights:
          'Support is slow, unresponsive, or overly reliant on AI/bots; users feel ignored, especially for billing or technical issues.',
        quote_source: {
          platform: 'trustpilot',
          review_id: '682462777e1b93c14f4c1b13'
        },
        mentions_count: 70,
        sentiment_score: -0.6,
        business_priority: 'high',
        representative_quote: [
          "I have been sent from pillar to post, with no solution in sight. No one is willing to resolve my issue. It's sad to see that all they care about is money because this happens after downgrading from their Guru Plan."
        ]
      },
      'Ease of Use / UX': {
        key_insights:
          'Interface is powerful but often overwhelming, unintuitive, or cluttered; onboarding and navigation are pain points for new users.',
        quote_source: {
          platform: 'trustpilot',
          review_id: '681b609c1e8058d6a249b1bc'
        },
        mentions_count: 60,
        sentiment_score: 0.1,
        business_priority: 'medium',
        representative_quote: [
          "Tricky platform to navigate. About half of the time I end up giving up because I'm not sure if it's hidden and I'm looking in the wrong place or the feature doesn't exist."
        ]
      },
      'Reporting & Analytics': {
        key_insights: 'Reporting and analytics are detailed and actionable, but some users want more customization and easier exports.',
        quote_source: {
          platform: 'trustpilot',
          review_id: '66c8aff4a8a35b357586a726'
        },
        mentions_count: 45,
        sentiment_score: 0.7,
        business_priority: 'medium',
        representative_quote: [
          "Great Reporting. It's been great being able to get consistent reporting and see the results our efforts have created."
        ]
      },
      'Cancellation & Billing': {
        key_insights: 'Users face extreme friction, unclear policies, and unauthorized charges when canceling or managing billing.',
        quote_source: {
          platform: 'trustpilot',
          review_id: '67988f56252f906cd6d6b6bc'
        },
        mentions_count: 110,
        sentiment_score: -0.85,
        business_priority: 'high',
        representative_quote: [
          "I have been charged £130 despite trying to cancel and receiving an expired subscription email from SEMrush. Have explained the situation to their customer support who don't want to listen to the facts and just keep repeating their 7 day refund policy. Avoid!"
        ]
      },
      'Feature Set & Innovation': {
        key_insights:
          'Praised for breadth and depth of SEO tools, frequent updates, and AI features; some feel overwhelmed by too many features.',
        quote_source: {
          platform: 'trustpilot',
          review_id: '6824a4124a362f7229480545'
        },
        mentions_count: 120,
        sentiment_score: 0.65,
        business_priority: 'medium',
        representative_quote: ['The tools are great and constantly improving with new features, including AI.']
      }
    },
    delight_features: {
      'Position Tracking': {
        quotes: ['Love the position tracking, comparison with competitors, keyword gap analysis.'],
        frequency: 35,
        strategic_value: 'high',
        enthusiasm_score: 0.9
      },
      'AI & Content Tools': {
        quotes: ['The tools are great and constantly improving with new features, including AI.'],
        frequency: 25,
        strategic_value: 'medium',
        enthusiasm_score: 0.8
      },
      'Keyword Magic Tool': {
        quotes: [
          "My favorite thing about Semrush is the Keyword Magic Tool! It's a fantastic help when identifying related keywords, and the monthly search volume seems to be more accurate than other tools I've used."
        ],
        frequency: 40,
        strategic_value: 'high',
        enthusiasm_score: 0.95
      },
      'Reporting & Analytics': {
        quotes: ["Great Reporting. It's been great being able to get consistent reporting and see the results our efforts have created."],
        frequency: 30,
        strategic_value: 'medium',
        enthusiasm_score: 0.85
      },
      'Comprehensive SEO Toolset': {
        quotes: [
          "Semrush is my go-to for all things SEO. It's packed with tools for keyword research, competitive analysis, backlink audits, and site health checks."
        ],
        frequency: 80,
        strategic_value: 'high',
        enthusiasm_score: 0.92
      }
    },
    feature_requests: {
      workarounds: [
        {
          issue: 'Unable to cancel subscription via platform',
          severity: 'high',
          frequency: 10,
          workaround:
            'Eventually cancelled it after 2 hours of attempts as I refuse to pay £120+ for such a bad service, would have cancelled my credit card before I payed that to this company.',
          business_impact: 'high'
        },
        {
          issue: 'Limited access to features on lower tiers',
          severity: 'medium',
          frequency: 7,
          workaround:
            'I went back to the free plan. $139/month for a small business is expensive, especially when the learning curve looks to take a year.',
          business_impact: 'medium'
        }
      ],
      formal_requests: [
        {
          request: 'One-click, self-service cancellation button',
          urgency: 'high',
          citation:
            'they push you with so many channels to try their product and in the end they on purpose have created a diabolic cancellation process. Man just put a button there called cancel subscription.',
          frequency: 25,
          business_value: 'high'
        },
        {
          request: 'More affordable pricing tiers for small businesses',
          urgency: 'high',
          citation:
            "The tool is very capable, but it's too expensive for small businesses. I wish they could have another option to match my reality.",
          frequency: 18,
          business_value: 'high'
        },
        {
          request: 'More transparent and upfront pricing',
          urgency: 'medium',
          citation:
            'Very deceptive pricing. Hidden fees everywhere. They entice with a lowball price but to get any effective services you need to add on very expensive additional services.',
          frequency: 12,
          business_value: 'high'
        },
        {
          request: 'Improved onboarding and in-app guidance',
          urgency: 'medium',
          citation:
            "I wish I had more time to explore all the ways Semrush could help. It's been great to have the information to share with colleagues about key words and key word gaps.",
          frequency: 8,
          business_value: 'medium'
        }
      ]
    },
    executive_summary: [
      'Cancellation and billing friction is the most severe pain point, driving anger, frustration, and churn; users demand a simple, self-service cancellation process.',
      'Pricing is widely perceived as expensive and deceptive, with constant upselling and hidden fees undermining trust and value.',
      'Customer support is a major weakness, with slow, unresponsive, or AI-driven responses failing to resolve critical issues.',
      'Despite these issues, the core SEO toolset, especially keyword research and reporting, is highly valued and drives daily use.',
      'Competitor threat from Ahrefs is significant, with users switching due to better flexibility, support, and transparent practices.'
    ],
    suggested_actions: [
      'Redesign the cancellation flow to be one-click, self-service, and transparent; eliminate email confirmation loops and hidden steps.',
      'Revamp pricing structure: introduce affordable, clearly defined plans for small businesses and reduce hidden fees and upselling.',
      'Invest in responsive, human customer support for billing and technical issues; reduce reliance on AI/bots for complex queries.',
      'Improve onboarding and in-app guidance to help new users navigate the platform and unlock value faster.',
      'Increase transparency around feature availability, plan differences, and renewal notifications to build trust and reduce disputes.'
    ],
    thematic_clusters: {
      'Pricing & Upselling': {
        frequency: 90,
        sentiment: 'negative',
        key_quotes: [
          'Feels like you are always nickel and dimed. Local SEO tracking is also way too expensive.',
          'Very deceptive pricing. Hidden fees everywhere.'
        ],
        business_impact: 'high'
      },
      'Customer Support Quality': {
        frequency: 70,
        sentiment: 'negative',
        key_quotes: [
          'No reply from support.',
          'The customer support is practically non-existent. Email responses take over 3 days, there is no clear way to reach a live person quickly.'
        ],
        business_impact: 'high'
      },
      'Ease of Use & Learning Curve': {
        frequency: 60,
        sentiment: 'mixed',
        key_quotes: [
          'Tricky platform to navigate.',
          'The interface can feel overwhelming at first, and some reports could be more intuitive to navigate.'
        ],
        business_impact: 'medium'
      },
      'Feature Breadth & Innovation': {
        frequency: 120,
        sentiment: 'positive',
        key_quotes: [
          'The tools are great and constantly improving with new features, including AI.',
          "Semrush is my go-to for all things SEO. It's packed with tools for keyword research, competitive analysis, backlink audits, and site health checks."
        ],
        business_impact: 'medium'
      },
      'Cancellation Friction & Billing Complaints': {
        frequency: 110,
        sentiment: 'negative',
        key_quotes: [
          'They make it very hard for you to cancel your plan.',
          'Such a scam. They make it impossible to cancel and have to send multiple emails which never arrive and then when they do they go to an infinite loading screen so you give up.'
        ],
        business_impact: 'high'
      }
    },
    emotional_analysis: {
      joy: {
        frequency: 120,
        percentage: 30,
        business_impact: 'medium',
        representative_quotes: [
          'Semrush is my go-to for all things SEO.',
          'Love the position tracking, comparison with competitors, keyword gap analysis.'
        ]
      },
      anger: {
        frequency: 80,
        percentage: 20,
        business_impact: 'high',
        representative_quotes: ['This company is a scam and will not allow you to cancel', 'Awful Unethical Company, DO NOT SIGN UP.']
      },
      other: {
        frequency: 40,
        percentage: 10,
        business_impact: 'low',
        representative_quotes: ['I will 1000% get the tattoo of the SEMRush logo if it includes the Business Plan / Access to the API']
      },
      confusion: {
        frequency: 60,
        percentage: 15,
        business_impact: 'medium',
        representative_quotes: ['Tricky platform to navigate.', 'The interface can feel overwhelming at first.']
      },
      frustration: {
        frequency: 100,
        percentage: 25,
        business_impact: 'high',
        representative_quotes: ['I am extremely frustrated with the cancellation process.', 'No reply from support.']
      },
      churn_signals: {
        'Switching to Competitor': {
          quotes: [
            "We've decided to cancel all our licences with SEMRush today and use Ahref's instead.",
            'I will also only hire agencies that use AHrefs opposed to SEMRUSH.'
          ],
          severity: 'high',
          frequency: 18,
          business_priority: 'high'
        },
        'Refund/Chargeback Threat': {
          quotes: [
            'I have had to file a dispute with my credit card company.',
            "I've filed with the BBB and FTC. Posting this so others are aware."
          ],
          severity: 'high',
          frequency: 25,
          business_priority: 'high'
        }
      }
    },
    competitive_analysis: {
      Moz: {
        mentions: 5,
        switching_reasons: [
          "If they do, I'd be more than happy to start recommending them again. If not, I'd sadly recommend marketers explore Ahrefs or even a simpler solution like Moz instead."
        ],
        comparative_advantages: ['Moz is more user friendly.'],
        competitive_threat_level: 'medium',
        comparative_disadvantages: [
          'Semrush is a powerful all-in-one digital marketing platform that excels at keyword research and backlink analysis.'
        ]
      },
      Ahrefs: {
        mentions: 18,
        switching_reasons: [
          'At times, it feels like Semrush is chasing competitors like Ahrefs rather than staying true to what made it special.',
          "Zero leniency regarding their usage policy. We've decided to cancel all our licences with SEMRush today and use Ahref's instead."
        ],
        comparative_advantages: [
          'Ahrefs does the same job (arguably more capable now) and they have been far more flexible with us.',
          'Almost everyone who does SEO seriously uses Ahrefs.'
        ],
        competitive_threat_level: 'high',
        comparative_disadvantages: [
          "Semrush is much more helpful than the last SEO tool I was using. Give me clear actionable items and I'm really loving it.",
          'semrush makes it easy for me to track local keyword rankings and unlike ahrefs, they also generate a nifty PDF report.'
        ]
      },
      'SE Ranking': {
        mentions: 2,
        switching_reasons: ['Unlike similar SaaS tools (like SE Ranking), there was no intuitive or self-service way to cancel.'],
        comparative_advantages: ['SE Ranking offers transparent opt-out buttons and real-time alerts.'],
        competitive_threat_level: 'low',
        comparative_disadvantages: [
          "Semrush's approach, by contrast, leaves global users at risk of paying for a subscription they never intended to keep."
        ]
      }
    },
    integration_requests: {
      'Looker Studio': {
        priority: 'medium',
        frequency: 4,
        use_cases: [
          "We're on the Pro plan (c. $1840/year) and don't have access to any API data (0 'credits') and can't integrate with Looker or other tools (all costs extra)."
        ],
        business_value: 'medium'
      },
      'Google Analytics / Search Console': {
        priority: 'medium',
        frequency: 8,
        use_cases: ['I connected all my information from Google Analytics and Google Search Console, and I can easily compare the data.'],
        business_value: 'high'
      }
    },
    user_journey_analysis: {
      phase: {
        support: {
          frequency: 70,
          sentiment: -0.7,
          key_insights: [
            'Support is slow, unresponsive, or overly reliant on AI/bots.',
            'Billing and cancellation issues are rarely resolved satisfactorily.'
          ],
          business_priority: 'high'
        },
        upgrade: {
          frequency: 20,
          sentiment: -0.3,
          key_insights: [
            'Upgrades are frequently required to access core features, leading to frustration.',
            'Users feel penalized for being loyal or early adopters.'
          ],
          business_priority: 'medium'
        },
        daily_use: {
          frequency: 120,
          sentiment: 0.6,
          key_insights: [
            'Users value the comprehensive toolset, especially for keyword research, tracking, and reporting.',
            'Some find the interface overwhelming or unintuitive.'
          ],
          business_priority: 'high'
        },
        discovery: {
          frequency: 30,
          sentiment: 0.4,
          key_insights: [
            'Product is widely recognized and often recommended for SEO and digital marketing.',
            'Some users discover Semrush via demos or referrals but are later disappointed by pricing or support.'
          ],
          business_priority: 'medium'
        },
        evaluation: {
          frequency: 40,
          sentiment: 0.1,
          key_insights: [
            'Demos and trials are promising, but users feel misled about feature availability and pricing.',
            'Confusion over plan differences and hidden costs is common.'
          ],
          business_priority: 'high'
        },
        onboarding: {
          frequency: 25,
          sentiment: -0.2,
          key_insights: [
            'Onboarding is often self-serve and overwhelming; users want more guided support.',
            'Some users never receive promised onboarding sessions.'
          ],
          business_priority: 'medium'
        }
      }
    },
    suspected_fake_reviews: {
      count: 18,
      detection_patterns: [
        {
          pattern: 'generic praise without specifics',
          examples: [
            'Great software, thank you.',
            'Excellent company.',
            'Great product great Service.',
            'Great tool for seo',
            'Great Reporting'
          ],
          frequency: 10,
          sentiment: 'positive'
        },
        {
          pattern: 'abnormally short positive reviews with no substance',
          examples: [
            'its so detailed',
            'Very useful company.i use it for my website analysis always and fi d it helpful especially which keywords are ranking right now is my most concerned thing',
            'best tool for seo tracking',
            'Great company | Very Expensive',
            'Great people to work with'
          ],
          frequency: 5,
          sentiment: 'positive'
        },
        {
          pattern: 'formulaic language across multiple reviews',
          examples: [
            'Semrush is a powerhouse for digital marketers',
            'Semrush Rocks',
            'Semrush has made a tremendous impact on my business with the ability to do keyword research. I highly recommend it to marketers and other businesses.'
          ],
          frequency: 3,
          sentiment: 'positive'
        }
      ],
      percentage_of_total: 4.5,
      sentiment_breakdown: {
        neutral: 1,
        negative: 0,
        positive: 17
      },
      representative_examples: ['Great software, thank you.', 'Excellent company.', 'its so detailed']
    }
  },
  rating_percentages: {
    count: {
      g2: 197,
      trustpilot: 200
    },
    sentiment: {
      neutral: 4,
      negative: 30,
      positive: 66
    },
    fake_reviews: {
      fake_by_source: {
        g2: 0,
        trustpilot: 0
      },
      total_fake_count: 0,
      fake_by_sentiment: {
        neutral: 0,
        negative: 0,
        positive: 0
      },
      total_fake_percentage: 0
    },
    average_rating: 3.6,
    adjusted_counts: {
      total: 397,
      neutral: 15,
      negative: 119,
      positive: 263
    }
  },
  project: null,
  success_status: true,
  processing_status: false,
  history: true
};

const dataPerriod = {
  data: [
    {
      time_period: '2024-05',
      average_rating: null,
      review_count: 0
    },
    {
      time_period: '2024-06',
      average_rating: null,
      review_count: 0
    },
    {
      time_period: '2024-07',
      average_rating: 3.79,
      review_count: 14
    },
    {
      time_period: '2024-08',
      average_rating: 3.62,
      review_count: 21
    },
    {
      time_period: '2024-09',
      average_rating: 3.25,
      review_count: 20
    },
    {
      time_period: '2024-10',
      average_rating: 2.98,
      review_count: 22
    },
    {
      time_period: '2024-11',
      average_rating: 3.24,
      review_count: 21
    },
    {
      time_period: '2024-12',
      average_rating: 3.87,
      review_count: 31
    },
    {
      time_period: '2025-01',
      average_rating: 3.83,
      review_count: 34
    },
    {
      time_period: '2025-02',
      average_rating: 3.62,
      review_count: 56
    },
    {
      time_period: '2025-03',
      average_rating: 3.51,
      review_count: 47
    },
    {
      time_period: '2025-04',
      average_rating: 3.38,
      review_count: 38
    },
    {
      time_period: '2025-05',
      average_rating: 3.73,
      review_count: 95
    }
  ],
  metadata: {
    task_id: '7d0104b9-6d10-431b-bc4c-85e4261f4f59',
    period: 'month',
    start_date: '2024-05-26',
    end_date: '2025-05-26',
    total_reviews: 399
  }
};

export { dataPerriod };

const ReviewsExampleReport = () => {
  const dispatch = useDispatch();

  useEffect(() => {
    // Add a small delay to ensure the component is mounted
    const timer = setTimeout(() => {
      dispatch(setReviewsAnalysisResults(exempleReport));
    }, 100);

    return () => clearTimeout(timer);
  }, [dispatch]);

  return (
    <Box pt={2} sc={{ display: 'flex', justifyContent: 'center' }}>
      <ReviewsV2Container exampleReport={true} />
    </Box>
  );
};

export default ReviewsExampleReport;
